import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  collection,
  getCountFromServer,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../firebase";

function StatCard({ title, value, subtitle, icon, tone = "blue", to }) {
  return (
    <Link to={to || "#"} className={`ad-card ad-stat-card ad-tone-${tone}`}>
      <div className="ad-stat-top">
        <div className="ad-stat-icon">{icon}</div>
        <div className="ad-stat-meta">
          <div className="ad-stat-title">{title}</div>
          <div className="ad-stat-value">{value}</div>
        </div>
      </div>
      {subtitle ? <div className="ad-stat-subtitle">{subtitle}</div> : null}
    </Link>
  );
}

function QuickActionCard({ title, text, to, icon, tone = "dark" }) {
  return (
    <Link to={to} className={`ad-card ad-quick-card ad-quick-${tone}`}>
      <div className="ad-quick-icon">{icon}</div>
      <div className="ad-quick-body">
        <h3>{title}</h3>
        <p>{text}</p>
      </div>
      <div className="ad-quick-arrow">→</div>
    </Link>
  );
}

function ModuleCard({ title, text, to, badge, icon }) {
  return (
    <Link to={to} className="ad-card ad-module-card">
      <div className="ad-module-head">
        <div className="ad-module-icon">{icon}</div>
        {badge ? <span className="ad-badge">{badge}</span> : null}
      </div>
      <h3>{title}</h3>
      <p>{text}</p>
      <span className="ad-module-link">Ouvrir le module →</span>
    </Link>
  );
}

function SectionTitle({ title, subtitle }) {
  return (
    <div className="ad-section-head">
      <h2>{title}</h2>
      {subtitle ? <p>{subtitle}</p> : null}
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState({
    users: "—",
    orders: "—",
    products: "—",
    pendingProducts: "—",
    pendingVendorRequests: "—",
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const loadStats = async () => {
      try {
        setLoading(true);

        const [
          usersSnap,
          ordersSnap,
          productsSnap,
          pendingProductsSnap,
          pendingVendorRequestsSnap,
        ] = await Promise.all([
          getCountFromServer(collection(db, "users")),
          getCountFromServer(collection(db, "orders")),
          getCountFromServer(collection(db, "productsShop")),
          getCountFromServer(
            query(
              collection(db, "productsShop"),
              where("validationStatus", "==", "pending")
            )
          ),
          getCountFromServer(
            query(
              collection(db, "vendorRequests"),
              where("status", "==", "pending_admin_validation")
            )
          ),
        ]);

        if (cancelled) return;

        setStats({
          users: usersSnap.data().count ?? 0,
          orders: ordersSnap.data().count ?? 0,
          products: productsSnap.data().count ?? 0,
          pendingProducts: pendingProductsSnap.data().count ?? 0,
          pendingVendorRequests: pendingVendorRequestsSnap.data().count ?? 0,
        });
      } catch (err) {
        console.error("Erreur chargement stats admin:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadStats();

    return () => {
      cancelled = true;
    };
  }, []);

  const summaryText = useMemo(() => {
    if (loading) return "Chargement des indicateurs administrateur…";
    return "Vue d’ensemble de la gestion GoShop : utilisateurs, vendeurs, produits, commandes et contenus marketing.";
  }, [loading]);

  return (
    <div className="admin-dashboard-page">
      <div className="container py-4">
        {/* HERO */}
        <section className="ad-hero">
          <div className="ad-hero-left">
            <span className="ad-kicker">Administration GoShop</span>
            <h1>Dashboard administrateur</h1>
            <p>{summaryText}</p>

            <div className="ad-hero-actions">
              <Link to="/admin/orders" className="ad-btn ad-btn-primary">
                Gérer les commandes
              </Link>
              <Link
                to="/admin/vendor-products-validation"
                className="ad-btn ad-btn-secondary"
              >
                Valider les produits
              </Link>
            </div>
          </div>

          <div className="ad-hero-right">
            <div className="ad-hero-panel">
              <div className="ad-hero-panel-label">Actions en attente</div>
              <div className="ad-hero-panel-value">
                {(Number(stats.pendingProducts || 0) || 0) +
                  (Number(stats.pendingVendorRequests || 0) || 0)}
              </div>
              <div className="ad-hero-panel-sub">
                Produits à valider + demandes vendeurs à examiner
              </div>
            </div>
          </div>
        </section>

        {/* STATS */}
        <section className="mt-4">
          <SectionTitle
            title="Vue rapide"
            subtitle="Les chiffres clés les plus utiles pour commencer la journée."
          />

          <div className="ad-stats-grid">
            <StatCard
              title="Utilisateurs"
              value={stats.users}
              subtitle="Comptes enregistrés"
              icon="👥"
              tone="blue"
              to="/admin/users"
            />

            <StatCard
              title="Commandes"
              value={stats.orders}
              subtitle="Toutes les commandes"
              icon="🛒"
              tone="green"
              to="/admin/orders"
            />

            <StatCard
              title="Produits"
              value={stats.products}
              subtitle="Catalogue total"
              icon="📦"
              tone="purple"
              to="/admin/vendor-products"
            />

            <StatCard
              title="Produits en attente"
              value={stats.pendingProducts}
              subtitle="À valider par l’admin"
              icon="⏳"
              tone="orange"
              to="/admin/vendor-products-validation"
            />

            <StatCard
              title="Demandes vendeurs"
              value={stats.pendingVendorRequests}
              subtitle="Demandes à examiner"
              icon="🏪"
              tone="red"
              to="/admin/vendor-requests"
            />
          </div>
        </section>

        {/* ACTIONS PRIORITAIRES */}
        <section className="mt-5">
          <SectionTitle
            title="Actions prioritaires"
            subtitle="Les accès les plus importants pour le pilotage quotidien."
          />

          <div className="ad-quick-grid">
            <QuickActionCard
              title="Demandes vendeurs"
              text="Examiner, valider ou refuser les nouvelles demandes d’inscription vendeur."
              to="/admin/vendor-requests"
              icon="📝"
              tone="amber"
            />

            <QuickActionCard
              title="Validation produits"
              text="Contrôler les nouveaux produits ou les modifications soumises par les vendeurs."
              to="/admin/vendor-products-validation"
              icon="✅"
              tone="green"
            />

            <QuickActionCard
              title="Commandes"
              text="Suivre l’ensemble des commandes, leurs statuts et les détails clients."
              to="/admin/orders"
              icon="🚚"
              tone="blue"
            />
          </div>
        </section>

        {/* MODULES PRINCIPAUX */}
        <section className="mt-5">
          <SectionTitle
            title="Modules d’administration"
            subtitle="Accédez rapidement à tous les espaces de gestion."
          />

          <div className="ad-modules-grid">
            <ModuleCard
              title="Utilisateurs"
              text="Gérer les comptes utilisateurs, rôles et accès."
              to="/admin/users"
              badge="Gestion comptes"
              icon="👤"
            />

            <ModuleCard
              title="Commandes"
              text="Voir, filtrer et mettre à jour les commandes du site."
              to="/admin/orders"
              badge="E-commerce"
              icon="🧾"
            />

            <ModuleCard
              title="Demandes vendeurs"
              text="Valider les demandes de boutiques et suivre les inscriptions."
              to="/admin/vendor-requests"
              badge="Vendeurs"
              icon="🏬"
            />

            <ModuleCard
              title="Validation produits"
              text="Contrôler les produits à publier ou à corriger."
              to="/admin/vendor-products-validation"
              badge="Publication"
              icon="📋"
            />

            <ModuleCard
              title="Produits vendeurs"
              text="Accéder à la vue admin des produits publiés par les vendeurs."
              to="/admin/vendor-products"
              badge="Catalogue"
              icon="📦"
            />

            <ModuleCard
              title="Catégories"
              text="Créer et organiser les catégories et sous-catégories."
              to="/admin/categories"
              badge="Structure"
              icon="🗂️"
            />

            <ModuleCard
              title="Bannières héro"
              text="Gérer les grands sliders et visuels principaux."
              to="/admin/herobanners"
              badge="Marketing"
              icon="🖼️"
            />

            <ModuleCard
              title="Hero grids"
              text="Modifier les grilles visuelles d’accueil et mises en avant."
              to="/admin/herogrids"
              badge="Accueil"
              icon="🔲"
            />

            <ModuleCard
              title="Deals slider"
              text="Configurer le slider des promotions et offres du moment."
              to="/admin/deals-slider"
              badge="Promotions"
              icon="🎯"
            />

            <ModuleCard
              title="Deals shelves"
              text="Gérer les rayons promotionnels et sélections temporaires."
              to="/admin/deals-shelves"
              badge="Promos"
              icon="🛍️"
            />

            <ModuleCard
              title="Vidéos mises en avant"
              text="Administrer les vidéos visibles sur le site."
              to="/admin/featured-videos"
              badge="Contenu"
              icon="🎥"
            />

            <ModuleCard
              title="Rayons sponsorisés"
              text="Créer et organiser les blocs sponsorisés sur le site."
              to="/admin/sponsored"
              badge="Monétisation"
              icon="⭐"
            />

            <ModuleCard
              title="Mise à jour pending"
              text="Outil technique pour remettre certains produits en statut pending."
              to="/admin/update-pending"
              badge="Maintenance"
              icon="🛠️"
            />
            <ModuleCard
              title="Paiements vendeurs"
              text="Suivre les paiements et commissions liés aux vendeurs."
              to="/admin/vendor-payments"
              badge="Finances"
              icon="💰"
            />
            <ModuleCard
              title="Deals sponsorisés"
              text="Gérer les offres sponsorisées et leur visibilité sur le site."
              to="/admin/sponsored-deals"
              badge="Monétisation"
              icon="🔥"
            />
            <ModuleCard
              title="Payouts affiliés"
              text="Suivre et gérer les paiements liés au programme d’affiliation."
              to="/admin/affiliate-payouts"
              badge="Affiliation"
              icon="🤝"
            />
          </div>
        </section>

        {/* RACCOURCIS VERS PRODUITS D’UN VENDEUR */}
        <section className="mt-5">
          <SectionTitle
            title="Gestion avancée vendeurs"
            subtitle="Accès directs pour le suivi des catalogues vendeurs."
          />

          <div className="ad-callout">
            <div className="ad-callout-left">
              <h3>Produits d’un vendeur spécifique</h3>
              <p>
                Depuis la liste des vendeurs ou des demandes validées, ouvre
                directement le catalogue d’un vendeur via l’URL :
                <code>/admin/vendor/:vendorId/products</code>
              </p>
            </div>

            <div className="ad-callout-right">
              <Link to="/admin/vendor-products" className="ad-btn ad-btn-primary">
                Voir la vue admin produits
              </Link>
            </div>
          </div>
        </section>
      </div>

      <style>{`
        .admin-dashboard-page{
          background:#f5f7fb;
          min-height:100vh;
        }

        .ad-hero{
          display:grid;
          grid-template-columns:minmax(0,1.4fr) 320px;
          gap:18px;
          align-items:stretch;
        }

        .ad-hero-left,
        .ad-hero-right{
          background:#fff;
          border:1px solid #e5e7eb;
          border-radius:24px;
          box-shadow:0 12px 30px rgba(15,23,42,.06);
        }

        .ad-hero-left{
          padding:24px;
        }

        .ad-hero-right{
          padding:20px;
          display:flex;
          align-items:center;
          justify-content:center;
        }

        .ad-kicker{
          display:inline-flex;
          padding:6px 12px;
          border-radius:999px;
          background:#ede9fe;
          color:#5b21b6;
          font-size:11px;
          font-weight:900;
          text-transform:uppercase;
          letter-spacing:.08em;
          margin-bottom:10px;
        }

        .ad-hero h1{
          margin:0 0 10px;
          font-size:34px;
          line-height:1.1;
          font-weight:900;
          color:#0f172a;
        }

        .ad-hero p{
          margin:0;
          color:#64748b;
          font-size:14px;
          line-height:1.7;
          max-width:760px;
        }

        .ad-hero-actions{
          display:flex;
          flex-wrap:wrap;
          gap:10px;
          margin-top:18px;
        }

        .ad-btn{
          display:inline-flex;
          align-items:center;
          justify-content:center;
          min-height:44px;
          padding:10px 18px;
          border-radius:999px;
          text-decoration:none;
          font-size:14px;
          font-weight:800;
          transition:.2s ease;
          border:1px solid transparent;
        }

        .ad-btn-primary{
          background:#1d4ed8;
          color:#fff;
          border-color:#1d4ed8;
        }

        .ad-btn-primary:hover{
          background:#1e40af;
          color:#fff;
        }

        .ad-btn-secondary{
          background:#fff;
          color:#111827;
          border-color:#d1d5db;
        }

        .ad-btn-secondary:hover{
          background:#f8fafc;
          color:#111827;
        }

        .ad-hero-panel{
          width:100%;
          background:linear-gradient(135deg,#111827 0%,#1e293b 100%);
          color:#fff;
          border-radius:20px;
          padding:22px 20px;
          box-shadow:0 16px 35px rgba(15,23,42,.20);
        }

        .ad-hero-panel-label{
          font-size:12px;
          color:#cbd5e1;
          text-transform:uppercase;
          letter-spacing:.08em;
          font-weight:800;
        }

        .ad-hero-panel-value{
          font-size:42px;
          font-weight:900;
          line-height:1;
          margin:10px 0 8px;
        }

        .ad-hero-panel-sub{
          color:#e2e8f0;
          font-size:13px;
          line-height:1.6;
        }

        .ad-section-head{
          margin-bottom:14px;
        }

        .ad-section-head h2{
          margin:0;
          font-size:22px;
          font-weight:900;
          color:#0f172a;
        }

        .ad-section-head p{
          margin:4px 0 0;
          color:#64748b;
          font-size:13px;
        }

        .ad-stats-grid{
          display:grid;
          grid-template-columns:repeat(5, minmax(0, 1fr));
          gap:14px;
        }

        .ad-card{
          text-decoration:none;
          color:inherit;
          background:#fff;
          border:1px solid #e5e7eb;
          border-radius:22px;
          box-shadow:0 10px 24px rgba(15,23,42,.05);
          transition:.2s ease;
        }

        .ad-card:hover{
          transform:translateY(-2px);
          box-shadow:0 18px 36px rgba(15,23,42,.10);
        }

        .ad-stat-card{
          padding:16px;
        }

        .ad-stat-top{
          display:flex;
          align-items:center;
          gap:12px;
        }

        .ad-stat-icon{
          width:48px;
          height:48px;
          border-radius:16px;
          display:flex;
          align-items:center;
          justify-content:center;
          font-size:22px;
          flex-shrink:0;
        }

        .ad-tone-blue .ad-stat-icon{ background:#dbeafe; }
        .ad-tone-green .ad-stat-icon{ background:#dcfce7; }
        .ad-tone-purple .ad-stat-icon{ background:#ede9fe; }
        .ad-tone-orange .ad-stat-icon{ background:#ffedd5; }
        .ad-tone-red .ad-stat-icon{ background:#fee2e2; }

        .ad-stat-title{
          font-size:13px;
          color:#64748b;
          font-weight:700;
        }

        .ad-stat-value{
          font-size:28px;
          line-height:1.1;
          font-weight:900;
          color:#111827;
          margin-top:2px;
        }

        .ad-stat-subtitle{
          margin-top:10px;
          font-size:12px;
          color:#6b7280;
        }

        .ad-quick-grid{
          display:grid;
          grid-template-columns:repeat(3, minmax(0, 1fr));
          gap:14px;
        }

        .ad-quick-card{
          padding:18px;
          display:flex;
          align-items:flex-start;
          gap:14px;
        }

        .ad-quick-icon{
          width:52px;
          height:52px;
          border-radius:16px;
          display:flex;
          align-items:center;
          justify-content:center;
          font-size:24px;
          flex-shrink:0;
          background:#f8fafc;
        }

        .ad-quick-body{
          flex:1;
        }

        .ad-quick-body h3{
          margin:0 0 6px;
          font-size:16px;
          font-weight:900;
          color:#111827;
        }

        .ad-quick-body p{
          margin:0;
          font-size:13px;
          color:#64748b;
          line-height:1.6;
        }

        .ad-quick-arrow{
          font-size:20px;
          color:#94a3b8;
          font-weight:900;
        }

        .ad-modules-grid{
          display:grid;
          grid-template-columns:repeat(4, minmax(0, 1fr));
          gap:14px;
        }

        .ad-module-card{
          padding:16px;
          min-height:210px;
          display:flex;
          flex-direction:column;
        }

        .ad-module-head{
          display:flex;
          justify-content:space-between;
          align-items:center;
          gap:10px;
          margin-bottom:12px;
        }

        .ad-module-icon{
          width:46px;
          height:46px;
          border-radius:14px;
          background:#f1f5f9;
          display:flex;
          align-items:center;
          justify-content:center;
          font-size:22px;
        }

        .ad-badge{
          display:inline-flex;
          align-items:center;
          padding:4px 8px;
          border-radius:999px;
          background:#f8fafc;
          border:1px solid #e5e7eb;
          color:#475569;
          font-size:11px;
          font-weight:800;
        }

        .ad-module-card h3{
          margin:0 0 8px;
          font-size:16px;
          font-weight:900;
          color:#0f172a;
        }

        .ad-module-card p{
          margin:0;
          color:#64748b;
          font-size:13px;
          line-height:1.65;
          flex:1;
        }

        .ad-module-link{
          margin-top:14px;
          font-size:13px;
          font-weight:800;
          color:#1d4ed8;
        }

        .ad-callout{
          display:grid;
          grid-template-columns:minmax(0,1fr) auto;
          gap:16px;
          align-items:center;
          background:#fff;
          border:1px solid #e5e7eb;
          border-radius:24px;
          box-shadow:0 10px 24px rgba(15,23,42,.05);
          padding:20px;
        }

        .ad-callout h3{
          margin:0 0 6px;
          font-size:18px;
          font-weight:900;
          color:#111827;
        }

        .ad-callout p{
          margin:0;
          font-size:13px;
          color:#64748b;
          line-height:1.7;
        }

        .ad-callout code{
          background:#f8fafc;
          padding:2px 6px;
          border-radius:8px;
          color:#334155;
          font-size:12px;
          margin-left:4px;
        }

        @media (max-width: 1200px){
          .ad-stats-grid{
            grid-template-columns:repeat(3, minmax(0, 1fr));
          }

          .ad-modules-grid{
            grid-template-columns:repeat(3, minmax(0, 1fr));
          }
        }

        @media (max-width: 992px){
          .ad-hero{
            grid-template-columns:minmax(0,1fr);
          }

          .ad-quick-grid{
            grid-template-columns:1fr;
          }

          .ad-modules-grid{
            grid-template-columns:repeat(2, minmax(0, 1fr));
          }

          .ad-callout{
            grid-template-columns:1fr;
          }
        }

        @media (max-width: 640px){
          .ad-hero h1{
            font-size:28px;
          }

          .ad-stats-grid{
            grid-template-columns:1fr;
          }

          .ad-modules-grid{
            grid-template-columns:1fr;
          }

          .ad-stat-value{
            font-size:24px;
          }
        }
      `}</style>
    </div>
  );
}